/**
 * useStandGorunumu.js — is this stand page shown to an organizer (backlog #510, 2026-10-20), and what may it do here?
 *
 * /Firma/Talepler/:standId is the exhibitor's page for ONE stand: it draws add / edit / save controls. The backend lets an organizer read it as well
 * (PortalService.standErisimi: a TALEPLER tier lets it read; it writes only through TALEP_EKLE and withdraws only through TALEP_SIL, both at the Denetim
 * tier), so an organizer at İzleme met buttons that the backend refuses, against #450's rule that İzleme draws no action at all.
 *
 * THE ORGANIZER VIEW is: the ACTIVE interface set is the organizer's (a company that is Organizatör and Dekoratör at one fair has two sets and switches in the
 * header: the set it chose decides) AND the company holds no Katılımcı / Dekoratör role on this stand (an organizer that is also the stand's exhibitor keeps
 * the exhibitor view, as before). Everything else, a company login with no portal context included, is the exhibitor view and nothing changes for it.
 *
 * The rules, for an organizer: İzleme (no Denetim in TALEPLER, or a staff preview) = `salt`: no action. Denetim = only what SAKA opened: `ekleyebilir`
 * (TALEP_EKLE: add and change a request) and `silebilir` (TALEP_SIL: withdraw one); a closed one is listed with its reason (`kapaliNedeni`, #418). These are VIEW
 * decisions; the backend answers the same questions on every write.
 */
import { useMemo } from 'react';
import { usePortal } from '../Contexts/PortalContext';

const SERBEST = {
  organizatorMu: false, salt: false, ekleyebilir: true, silebilir: true,
  izin: () => true, kapaliNedeni: () => null,
};

/** Pure: the view of a stand for the portal context and the stand ({ firmaId, dekoratorFirmaId }). */
export function standGorunumu(portal, stand) {
  const benimFirmaId = portal?.firma?.firmaId ?? null;
  const aktif = portal?.aktif ?? null;
  if (!aktif || aktif.rol !== 'ORGANIZATOR') return { ...SERBEST, benimFirmaId };
  const standdaRolu = benimFirmaId != null
    && [stand?.firmaId, stand?.dekoratorFirmaId].some((id) => id != null && Number(id) === Number(benimFirmaId));
  if (standdaRolu) return { ...SERBEST, benimFirmaId };

  const kademe = typeof portal.alan === 'function'
    ? portal.alan('TALEPLER')
    : (aktif.alanlar ?? []).find((a) => a.alan === 'TALEPLER');
  const denetleyebilir = Boolean(kademe?.denetleyebilir) && !portal.onizleme;
  const izin = (kod) => denetleyebilir && (typeof portal.eylem === 'function'
    ? Boolean(portal.eylem(kod)?.izinli)
    : Boolean((aktif.eylemler ?? []).find((e) => e.kod === kod)?.izinli));
  return {
    organizatorMu: true,
    salt: !denetleyebilir,
    ekleyebilir: izin('TALEP_EKLE'),
    silebilir: izin('TALEP_SIL'),
    izin,
    kapaliNedeni: (kod) => (typeof portal.eylemIpucu === 'function' ? portal.eylemIpucu(kod, aktif) : null),
    benimFirmaId,
  };
}

export default function useStandGorunumu(stand) {
  const portal = usePortal();
  return useMemo(() => standGorunumu(portal, stand), [portal, stand]);
}
